import { SiteFooter } from "@/components/nav/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Check, ExternalLink, Search, Sparkles, Ticket, Users, X, Zap } from "lucide-react";
import {
  DescriptionPreview,
  EventMeta,
  EventTile,
  HostBadge,
  IntroChip,
  MyRsvpPill,
  SeatsPill,
  TypeChip,
  WhenPill,
} from "@/components/events/EventTile";
import { listPublicEventsWithMeta } from "@/lib/events.functions";
import {
  DEFAULT_EVENT_TYPE,
  eventTypeHook,
  eventTypeLabel,
  externalRsvp,
  isNoRsvpEvent,
  formatEventDate,
  formatEventMonth,
  isCommunityEvent,
  isPast,
  proximityLabel,
  seatsPill,
  type SquadEvent,
} from "@/lib/events";
import { markdownPreview, markdownToPlainText } from "@/lib/markdown";
import { RSVP_EVENTS, rsvpContext, trackRsvp } from "@/lib/rsvp-telemetry";
import { SiteHeader } from "@/components/nav/SiteHeader";
import { useSessionNav } from "@/components/nav/useSessionNav";
import { EventsPageSkeleton } from "@/components/PageSkeletons";
import { getMyEventRsvpStatuses, type RsvpStatus } from "@/lib/rsvps.functions";
import { CREW_BOUNCE_NOTE, takeCrewBounce } from "@/lib/wrong-part-of-town";

export const Route = createFileRoute("/events")({
  loader: () => listPublicEventsWithMeta(),
  head: () => ({
    meta: [
      { title: "Las Vegas FPV Events Calendar — SquadFlight FPV" },
      {
        name: "description",
        content:
          "Find an FPV night to fly in Las Vegas this week — track sessions, freestyle, meetups and beginner-friendly intros. Open an event and grab a seat.",
      },
      { property: "og:title", content: "Las Vegas FPV Events Calendar — SquadFlight FPV" },
      {
        property: "og:description",
        content:
          "Track nights, freestyle sessions, meetups and beginner intros across the valley. Pick a night and RSVP.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://squadflight.com/events" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://squadflight.com/events" }],
  }),
  pendingComponent: EventsPageSkeleton,
  errorComponent: EventsError,
  notFoundComponent: EventsError,
  component: EventsPage,
});

function EventsError() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Events aren't loading</h1>
      <p className="mt-3 text-muted-foreground">
        Give it a refresh, or head back to the home page.
      </p>
      <Link to="/" className="mt-6 inline-block text-primary underline underline-offset-4">
        Back home
      </Link>
    </div>
  );
}

/** Shared analytics bag for an event on the list. Never carries personal data. */
function listContext(event: SquadEvent, going: number | undefined) {
  return rsvpContext({
    slug: event.slug,
    eventType: event.event_type ?? DEFAULT_EVENT_TYPE,
    rsvpMode: event.rsvp_mode ?? "internal",
    capacity: event.capacity ?? null,
    spotsLeft:
      event.capacity && event.capacity > 0
        ? Math.max(0, event.capacity - Math.max(0, going ?? 0))
        : null,
    introSession: event.is_intro_session ?? false,
  });
}

function trackOpen(event: SquadEvent, going: number | undefined, position: number, from: string) {
  trackRsvp(RSVP_EVENTS.listCardOpened, {
    ...listContext(event, going),
    position,
    from,
  });
}

/**
 * RSVPs land here by default. When a host runs signups elsewhere (MultiGP,
 * Partiful, ...) we're a directory for that night and link straight out.
 */
function RsvpButton({
  event,
  going,
  position,
  myStatus,
  size = "lg",
}: {
  event: SquadEvent;
  going?: number | undefined;
  position: number;
  myStatus?: RsvpStatus | undefined;
  size?: "sm" | "lg";
}) {
  const external = externalRsvp(event);
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-[image:var(--gradient-neon)] font-display font-semibold uppercase tracking-wide text-primary-foreground transition-transform hover:scale-[1.03] glow-primary ${
    size === "lg" ? "px-7 py-3.5 text-base" : "px-5 py-2.5 text-sm"
  }`;

  if (external) {
    return (
      <a
        href={external.url}
        target="_blank"
        rel="noreferrer noopener"
        className={cls}
        onClick={() =>
          trackRsvp(RSVP_EVENTS.externalClicked, {
            ...listContext(event, going),
            provider: external.mode,
            from: "events_list",
          })
        }
      >
        <Ticket className="size-4" />
        RSVP on {external.label}
        <ExternalLink className="size-4" />
      </a>
    );
  }

  if (isNoRsvpEvent(event)) {
    return (
      <Link
        to="/events/$slug"
        params={{ slug: event.slug }}
        className={cls}
        onClick={() => trackOpen(event, going, position, "cta")}
      >
        <Check className="size-4" />
        No RSVP · Just show up
      </Link>
    );
  }

  const statusLabel =
    myStatus === "going" || myStatus === "attended"
      ? "You're in · View details"
      : myStatus === "waitlisted"
        ? "You're waitlisted · View"
        : myStatus === "maybe"
          ? "You said maybe · Update"
          : myStatus === "cancelled"
            ? "RSVP again"
            : null;

  return (
    <Link
      to="/events/$slug"
      params={{ slug: event.slug }}
      className={cls}
      onClick={() => trackOpen(event, going, position, "cta")}
    >
      {statusLabel ? <Check className="size-4" /> : <Ticket className="size-4" />}
      {statusLabel ?? "See details & RSVP"}
      {!statusLabel && <Zap className="size-4 transition-transform group-hover:translate-x-0.5" />}
    </Link>
  );
}

function EventCard({
  event,
  going,
  position,
  myStatus,
}: {
  event: SquadEvent;
  going?: number | undefined;
  position: number;
  myStatus?: RsvpStatus | undefined;
}) {
  const upcoming = !isPast(event);
  return (
    <EventTile
      event={event}
      going={going}
      position={position}
      myStatus={myStatus}
      onOpen={(from) => trackOpen(event, going, position, from)}
      cta={
        upcoming ? (
          <RsvpButton
            event={event}
            going={going}
            position={position}
            myStatus={myStatus}
            size="sm"
          />
        ) : (
          <Link
            to="/events/$slug"
            params={{ slug: event.slug }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            onClick={() => trackOpen(event, going, position, "past")}
          >
            See how it went
          </Link>
        )
      }
    />
  );
}

type QuickPick = "this_week" | "beginner" | "free" | "community" | "squadflight";

const QUICK_PICKS: { key: QuickPick; label: string }[] = [
  { key: "this_week", label: "This week" },
  { key: "beginner", label: "New to FPV" },
  { key: "free", label: "Free" },
  { key: "community", label: "Community" },
  { key: "squadflight", label: "SquadFlight" },
];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function isFree(event: SquadEvent) {
  return (event.price_cents ?? 0) <= 0;
}

function isThisWeek(event: SquadEvent, now = Date.now()) {
  const start = new Date(event.starts_at).getTime();
  return !Number.isNaN(start) && start - now <= WEEK_MS;
}

function matchesQuickPick(event: SquadEvent, pick: QuickPick) {
  switch (pick) {
    case "this_week":
      return isThisWeek(event);
    case "beginner":
      return Boolean(event.is_intro_session);
    case "free":
      return isFree(event);
    case "community":
      return isCommunityEvent(event);
    case "squadflight":
      return !isCommunityEvent(event);
  }
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-display text-xs uppercase tracking-[0.15em] transition-colors ${
        active
          ? "border-primary bg-primary/15 text-primary"
          : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function EventsPage() {
  const { events, meta } = Route.useLoaderData();
  const nav = useSessionNav();
  const fetchMyStatuses = useServerFn(getMyEventRsvpStatuses);
  const { data: myStatuses = {} } = useQuery({
    queryKey: ["my-event-rsvp-statuses"],
    queryFn: () => fetchMyStatuses(),
    enabled: nav.signedIn === true,
    staleTime: 30_000,
    retry: false,
  });
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [picks, setPicks] = useState<QuickPick[]>([]);
  // ?q= lets other pages (like the not-found page) drop someone straight into
  // a filtered calendar.
  const [q, setQ] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("q") ?? "";
  });
  const [etype, setEtype] = useState<string>("all");
  // A pilot who tried a crew page lands here; one quiet line explains why.
  const [crewNote, setCrewNote] = useState(false);
  useEffect(() => {
    if (takeCrewBounce()) setCrewNote(true);
  }, []);

  const live = events.filter((e) => e.status === "planned" || e.status === "ended");
  const matches = (e: SquadEvent) => {
    for (const pick of picks) if (!matchesQuickPick(e, pick)) return false;
    if (etype !== "all" && (e.event_type ?? DEFAULT_EVENT_TYPE) !== etype) return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const haystack = `${e.title} ${e.location ?? ""} ${eventTypeLabel(e.event_type)} ${markdownToPlainText(e.description)}`;
    return haystack.toLowerCase().includes(needle);
  };

  const upcoming = live
    .filter((e) => !isPast(e))
    .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
  const past = live
    .filter((e) => isPast(e))
    .sort((a, b) => +new Date(b.starts_at) - +new Date(a.starts_at));

  const togglePick = (key: QuickPick) => {
    setPicks((prev) => {
      const next = prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key];
      trackRsvp(RSVP_EVENTS.listFilterUsed, { filter: key, active: !prev.includes(key) });
      return next;
    });
  };
  const filtersActive = picks.length > 0 || etype !== "all" || q.trim().length > 0;
  const clearFilters = () => {
    setPicks([]);
    setEtype("all");
    setQ("");
    trackRsvp(RSVP_EVENTS.listFilterUsed, { filter: "clear", active: true });
  };

  const heroPool = upcoming.filter(matches);
  const hero =
    tab === "upcoming" ? (heroPool.find((e) => e.featured) ?? heroPool[0] ?? null) : null;
  const heroStatus = hero ? myStatuses[hero.id] : undefined;
  const list = (tab === "upcoming" ? upcoming.filter((e) => e.id !== hero?.id) : past).filter(
    matches,
  );
  const matchCount = tab === "upcoming" ? heroPool.length : list.length;
  const nextUp = upcoming[0] ?? null;

  // Only offer type chips for kinds that actually have something upcoming.
  const availableTypes = Array.from(
    new Set(upcoming.map((e) => e.event_type ?? DEFAULT_EVENT_TYPE)),
  );
  const pickCount = (key: QuickPick) => upcoming.filter((e) => matchesQuickPick(e, key)).length;

  // Upcoming events get month headings so a long calendar scans quickly.
  const grouped: { label: string; items: SquadEvent[] }[] = [];
  if (tab === "upcoming") {
    for (const e of list) {
      const label = formatEventMonth(e.starts_at);
      const last = grouped[grouped.length - 1];
      if (last && last.label === label) last.items.push(e);
      else grouped.push({ label, items: [e] });
    }
  }
  let position = 0;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <SiteHeader
        cta={
          <Link
            to="/"
            className="hidden rounded-full border border-primary/50 px-4 py-2 text-sm text-primary hover:bg-primary/10 sm:inline-flex"
          >
            Join the waitlist
          </Link>
        }
      />

      {crewNote && (
        <div className="border-b border-primary/40 bg-primary/10">
          <div className="mx-auto flex max-w-5xl items-center gap-3 px-5 py-2 text-sm text-primary">
            <span className="flex-1">{CREW_BOUNCE_NOTE}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setCrewNote(false)}
              className="rounded p-1 hover:bg-primary/15"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-5xl px-5 py-14">
        <span className="inline-block rounded-full border border-border bg-secondary px-3 py-1 font-display text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Valley FPV Calendar
        </span>
        <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
          Pick a night. <span className="text-gradient-neon">Fly this week.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
          {upcoming.length > 0
            ? `${upcoming.length} FPV ${upcoming.length === 1 ? "night" : "nights"} coming up around Las Vegas — track sessions, freestyle, meetups and beginner intros. Open one to see the full rundown and grab a seat. Never flown? We hand you the goggles.`
            : "Track sessions, freestyle, meetups and beginner intros around Las Vegas. Nothing on the board this second — join the waitlist and you'll hear first."}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-border p-1">
            {(["upcoming", "past"] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`rounded-full px-4 py-2 text-sm capitalize transition-colors ${
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {key} ({key === "upcoming" ? upcoming.length : past.length})
              </button>
            ))}
          </div>
          <label className="relative flex-1 sm:max-w-xs">
            <span className="sr-only">Search events</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, place or type"
              className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {QUICK_PICKS.filter((p) => pickCount(p.key) > 0).map((p) => (
            <FilterChip
              key={p.key}
              active={picks.includes(p.key)}
              onClick={() => togglePick(p.key)}
            >
              {p.key === "beginner" && <Sparkles className="size-3.5" />}
              {p.label} ({pickCount(p.key)})
            </FilterChip>
          ))}
          {availableTypes.length > 1 &&
            availableTypes.map((t) => (
              <FilterChip
                key={t}
                active={etype === t}
                onClick={() => {
                  const next = etype === t ? "all" : t;
                  setEtype(next);
                  trackRsvp(RSVP_EVENTS.listFilterUsed, {
                    filter: `type:${t}`,
                    active: next === t,
                  });
                }}
              >
                {eventTypeLabel(t)}
              </FilterChip>
            ))}
          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 font-display text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground"
            >
              <X className="size-3.5" /> Clear filters
            </button>
          )}
          {filtersActive && (
            <span className="text-xs text-muted-foreground">
              {matchCount} {matchCount === 1 ? "night" : "nights"} match
            </span>
          )}
        </div>

        {hero && (
          <section className="mt-10 overflow-hidden rounded-3xl border border-primary/40 bg-card/70 glow-primary">
            {hero.cover_image_url && (
              <Link
                to="/events/$slug"
                params={{ slug: hero.slug }}
                className="block"
                onClick={() => trackOpen(hero, meta[hero.id]?.going, 0, "hero_image")}
              >
                <img
                  src={hero.cover_image_url}
                  alt={`${hero.title} cover`}
                  className="aspect-[21/9] w-full object-cover"
                />
              </Link>
            )}
            <div className="p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-display text-xs uppercase tracking-[0.2em] text-primary">
                  Next up
                </span>
                <WhenPill event={hero} />
                <TypeChip event={hero} />
                <SeatsPill event={hero} going={meta[hero.id]?.going} />
                {heroStatus ? <MyRsvpPill status={heroStatus} /> : null}
                {hero.is_intro_session && <IntroChip />}
              </div>
              <h2 className="mt-3 font-display text-3xl font-bold">
                <Link
                  to="/events/$slug"
                  params={{ slug: hero.slug }}
                  className="transition-colors hover:text-primary"
                  onClick={() => trackOpen(hero, meta[hero.id]?.going, 0, "hero_title")}
                >
                  {hero.title}
                </Link>
              </h2>
              {hero.subtitle ? (
                <p className="mt-1 text-lg font-semibold text-foreground">{hero.subtitle}</p>
              ) : null}
              <p className="mt-1 text-primary/90">{eventTypeHook(hero.event_type)}</p>
              <EventMeta event={hero} />
              <div className="mt-3">
                <HostBadge event={hero} />
              </div>
              <DescriptionPreview
                event={hero}
                max={200}
                onOpen={(from) => trackOpen(hero, meta[hero.id]?.going, 0, from)}
                className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground"
              />
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <RsvpButton
                  event={hero}
                  going={meta[hero.id]?.going}
                  position={0}
                  myStatus={myStatuses[hero.id]}
                />
                <span className="text-sm text-muted-foreground">
                  Takes about a minute — no account needed.
                </span>
              </div>
            </div>
          </section>
        )}

        {list.length > 0 ? (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold">
              {tab === "upcoming" ? "More nights to fly" : "Past events"}
            </h2>
            {tab === "upcoming" ? (
              grouped.map((group) => (
                <div key={group.label} className="mt-8">
                  <h3 className="font-display text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    {group.label}
                  </h3>
                  <div className="mt-4 grid gap-5">
                    {group.items.map((e) => (
                      <EventCard
                        key={e.id}
                        event={e}
                        going={meta[e.id]?.going}
                        position={++position}
                        myStatus={myStatuses[e.id]}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="mt-6 grid gap-5">
                {list.map((e) => (
                  <EventCard
                    key={e.id}
                    event={e}
                    going={meta[e.id]?.going}
                    position={++position}
                    myStatus={myStatuses[e.id]}
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
          !hero && (
            <section className="mt-12 rounded-3xl border border-border bg-card/60 p-10 text-center">
              <h2 className="font-display text-2xl font-bold">
                {filtersActive
                  ? "Nothing matches those filters"
                  : tab === "upcoming"
                    ? "Nothing on the calendar yet"
                    : "No past events yet"}
              </h2>
              {filtersActive ? (
                <>
                  <p className="mt-2 text-muted-foreground">
                    {nextUp
                      ? `Closest night on the board is ${nextUp.title} — ${proximityLabel(nextUp.starts_at).toLowerCase()}.`
                      : "Clear the filters to see the whole valley."}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                    {nextUp && (
                      <RsvpButton event={nextUp} going={meta[nextUp.id]?.going} position={0} />
                    )}
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                    >
                      Clear filters
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-2 text-muted-foreground">
                    No nights scheduled yet — join the waitlist and you'll hear first.
                  </p>
                  <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[image:var(--gradient-neon)] px-7 py-3.5 font-display text-base font-semibold uppercase tracking-wide text-primary-foreground glow-primary"
                  >
                    Join the waitlist <Zap className="size-4" />
                  </Link>
                </>
              )}
            </section>
          )
        )}

        <section className="mt-14 rounded-3xl border border-border bg-card/50 p-8">
          <h2 className="font-display text-2xl font-bold">Flying something of your own?</h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
            This calendar isn't just ours. If you run a fly night, a park meetup, or a race in the
            valley, put it up here so the rest of the pilots can find it — you keep the RSVP list
            and the FREQ board for your night.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link
              to="/submit-event"
              className="inline-flex items-center gap-2 rounded-full border border-primary/50 px-6 py-3 font-display text-sm font-semibold uppercase tracking-wide text-primary hover:bg-primary/10"
            >
              <Users className="size-4" /> Post your event
            </Link>
            <Link
              to="/start"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              New to FPV? See Proving Ground training
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
